import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { X, Pen, Type, RotateCcw, ShieldCheck } from 'lucide-react';
import Button from './ui/Button';
import Input from './ui/Input';

function agreementIsReviewed(el: HTMLElement): boolean {
  const slack = 16;
  if (el.scrollHeight - el.clientHeight <= slack) return true;
  return el.scrollHeight - el.scrollTop - el.clientHeight < slack;
}

export type SignaturePayload =
  | {
      signatureType: 'drawn';
      signatureDataUrl: string;
      signerName: string;
      signerEmail?: string;
      consentAcknowledged: true;
    }
  | {
      signatureType: 'typed';
      signatureTypedName: string;
      signerName: string;
      signerEmail?: string;
      consentAcknowledged: true;
    };

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSign: (payload: SignaturePayload) => Promise<void> | void;
  contractTitle: string;
  contractBody: string;
  defaultSignerName?: string;
  defaultSignerEmail?: string;
  submitting?: boolean;
}

type Mode = 'draw' | 'type';

export default function SignContractModal({
  isOpen,
  onClose,
  onSign,
  contractTitle,
  contractBody,
  defaultSignerName,
  defaultSignerEmail,
  submitting,
}: Props) {
  const [mode, setMode] = useState<Mode>('draw');
  const [signerName, setSignerName] = useState(defaultSignerName || '');
  const [signerEmail, setSignerEmail] = useState(defaultSignerEmail || '');
  const [typedName, setTypedName] = useState(defaultSignerName || '');
  const [consent, setConsent] = useState(false);
  const [reachedBottom, setReachedBottom] = useState(false);
  const [hasStroke, setHasStroke] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const padRef = useRef<SignatureCanvas | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (isOpen) {
      setSignerName(defaultSignerName || '');
      setSignerEmail(defaultSignerEmail || '');
      setTypedName(defaultSignerName || '');
      setMode('draw');
      setConsent(false);
      setHasStroke(false);
      setError(null);
      setTimeout(() => padRef.current?.clear(), 0);
    }
  }, [isOpen, defaultSignerName, defaultSignerEmail]);

  useLayoutEffect(() => {
    if (!isOpen) return;
    const el = scrollRef.current;
    if (!el) return;

    const syncReviewed = () => {
      setReachedBottom(agreementIsReviewed(el));
    };

    syncReviewed();
    const frame = window.requestAnimationFrame(syncReviewed);
    const ro = new ResizeObserver(syncReviewed);
    ro.observe(el);
    return () => {
      window.cancelAnimationFrame(frame);
      ro.disconnect();
    };
  }, [isOpen, contractBody]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !submitting) onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose, submitting]);

  if (!isOpen) return null;

  const missingSteps: string[] = [];
  if (!reachedBottom) missingSteps.push('Scroll the agreement to the end');
  if (!signerName.trim()) missingSteps.push('Enter your full legal name');
  if (mode === 'draw' && !hasStroke) missingSteps.push('Draw your signature');
  if (mode === 'type' && !typedName.trim()) missingSteps.push('Type your signature');
  if (!consent) missingSteps.push('Tick the consent box');
  const canSubmit = missingSteps.length === 0;

  const handleClearPad = () => {
    padRef.current?.clear();
    setHasStroke(false);
  };

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    setReachedBottom(agreementIsReviewed(el));
  };

  const handleSubmit = async () => {
    setError(null);
    try {
      if (mode === 'draw') {
        const pad = padRef.current;
        if (!pad || pad.isEmpty()) {
          setError('Please draw your signature.');
          return;
        }
        const dataUrl = pad.getTrimmedCanvas().toDataURL('image/png');
        await onSign({
          signatureType: 'drawn',
          signatureDataUrl: dataUrl,
          signerName: signerName.trim(),
          signerEmail: signerEmail.trim() || undefined,
          consentAcknowledged: true,
        });
      } else {
        await onSign({
          signatureType: 'typed',
          signatureTypedName: typedName.trim(),
          signerName: signerName.trim(),
          signerEmail: signerEmail.trim() || undefined,
          consentAcknowledged: true,
        });
      }
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } }; message?: string };
      setError(err.response?.data?.error || err.message || 'Could not record signature.');
    }
  };

  return (
    <div className="ph-no-capture fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={() => !submitting && onClose()} />
      <div className="relative glass-card w-full max-w-3xl mx-4 max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-glass">
          <div className="flex items-center gap-2">
            <ShieldCheck size={18} className="text-emerald-500 dark:text-emerald-300" />
            <h2 className="text-lg font-semibold tracking-tightest text-glass-text">Sign contract</h2>
          </div>
          <button
            type="button"
            onClick={() => !submitting && onClose()}
            className="text-glass-text-3 hover:text-glass-text"
            disabled={submitting}
          >
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto glass-scroll p-5 space-y-5">
          <div>
            <div className="text-xs text-glass-text-3 mb-1">Agreement</div>
            <div className="text-sm font-medium text-glass-text">{contractTitle}</div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-medium text-glass-text-2">
                Review the agreement
              </label>
              {reachedBottom ? (
                <span className="text-xs text-emerald-500 dark:text-emerald-300 font-medium">
                  ✓ Reviewed
                </span>
              ) : (
                <span className="text-xs text-amber-500 dark:text-amber-300 font-medium">
                  Scroll to the end
                </span>
              )}
            </div>
            <div
              ref={scrollRef}
              onScroll={handleScroll}
              className="h-56 overflow-y-auto glass-scroll rounded-xl border border-glass bg-glass-2 p-3 text-sm text-glass-text whitespace-pre-wrap leading-relaxed"
            >
              {contractBody || '—'}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-glass-text-2 mb-1">
                Full legal name *
              </label>
              <Input
                value={signerName}
                onChange={(e) => setSignerName(e.target.value)}
                placeholder="e.g. Jane Tan Li Ping"
                maxLength={200}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-glass-text-2 mb-1">
                Email (optional)
              </label>
              <Input
                value={signerEmail}
                onChange={(e) => setSignerEmail(e.target.value)}
                placeholder="you@example.com"
                type="email"
              />
            </div>
          </div>

          <div>
            <div className="flex gap-2 mb-2">
              <button
                type="button"
                onClick={() => setMode('draw')}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border transition ${
                  mode === 'draw'
                    ? 'bg-accent2 text-white border-accent2'
                    : 'bg-glass-2 border-glass text-glass-text-2 hover:bg-glass-3'
                }`}
              >
                <Pen size={14} /> Draw
              </button>
              <button
                type="button"
                onClick={() => setMode('type')}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border transition ${
                  mode === 'type'
                    ? 'bg-accent2 text-white border-accent2'
                    : 'bg-glass-2 border-glass text-glass-text-2 hover:bg-glass-3'
                }`}
              >
                <Type size={14} /> Type
              </button>
            </div>

            {mode === 'draw' ? (
              <div>
                <div className="relative rounded-xl border border-glass bg-white">
                  <SignatureCanvas
                    ref={padRef}
                    penColor="#0f172a"
                    onEnd={() => setHasStroke(true)}
                    canvasProps={{
                      className: 'w-full h-40 rounded-xl',
                      style: { touchAction: 'none' },
                    }}
                  />
                </div>
                <div className="flex items-center justify-between mt-2">
                  <span className="text-xs text-glass-text-3">Use your mouse, trackpad, or finger to sign.</span>
                  <button
                    type="button"
                    onClick={handleClearPad}
                    className="text-xs text-glass-text-2 hover:text-glass-text flex items-center gap-1"
                  >
                    <RotateCcw size={12} /> Clear
                  </button>
                </div>
              </div>
            ) : (
              <div>
                <Input
                  value={typedName}
                  onChange={(e) => setTypedName(e.target.value)}
                  placeholder="Type your full name"
                  maxLength={200}
                />
                <div
                  className="mt-2 h-24 flex items-center justify-center rounded-xl border border-glass bg-glass-2 text-3xl italic text-glass-text"
                  style={{ fontFamily: "'Brush Script MT','Lucida Handwriting','Segoe Script',cursive" }}
                >
                  {typedName.trim() || 'Your signature'}
                </div>
                <p className="text-xs text-glass-text-3 mt-1">
                  Typing your name above is accepted as an electronic signature.
                </p>
              </div>
            )}
          </div>

          <label className="flex gap-2 items-start text-sm text-glass-text-2 rounded-xl border border-glass bg-glass-2 p-3">
            <input
              type="checkbox"
              className="mt-0.5 accent-accent2"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
            />
            <span>
              I have read this agreement and I intend to be legally bound by its terms. I agree that my
              electronic signature has the same legal effect as a handwritten signature, and I consent to
              conducting this transaction electronically.
            </span>
          </label>

          {error && (
            <div className="text-sm text-rose-500 dark:text-rose-300 rounded-xl border border-rose-500/30 bg-rose-500/10 p-2">
              {error}
            </div>
          )}
        </div>

        <div className="p-4 border-t border-glass flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div className="min-w-0 flex-1">
            <div className="text-xs text-glass-text-3">
              Your IP address, device, and a cryptographic hash of this document will be recorded as
              tamper-evidence.
            </div>
            {!canSubmit && missingSteps.length > 0 ? (
              <p className="mt-1 text-xs text-amber-600 dark:text-amber-300">
                Still needed: {missingSteps.join(' · ')}
              </p>
            ) : null}
          </div>
          <div className="flex gap-2 shrink-0">
            <Button variant="outline" onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={!canSubmit || submitting}>
              {submitting ? 'Signing…' : 'Sign contract'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
